import { hasAccess } from "@spiriidk/auth";
import type {
  Permission,
  ConnectMenuItem,
  MenuItemFeatureFlag,
} from "@spiriidk/connect-configs";
import type { FeatureFlagConfiguration } from "@spiriidk/feature-flags";
import { useMemo } from "react";

type MenuFlagValues = Map<FeatureFlagConfiguration, boolean>;

interface UseFilteredMenuItemsParams<T extends ConnectMenuItem> {
  menuItems: T[];
  userPermissions: Permission[];
  isBetaEnabled?: boolean;
  isSpiriiAdminUser?: boolean;
  /** Every flag the menu declares, resolved by the caller. An unresolved flag reads as disabled. */
  flagValues?: MenuFlagValues;
}

const NO_FLAG_VALUES: MenuFlagValues = new Map();

function isVisibleForFlag(
  { flag, visibleWhen }: MenuItemFeatureFlag,
  flagValues: MenuFlagValues
): boolean {
  const isEnabled = flagValues.get(flag) ?? false;
  return visibleWhen === "enabled" ? isEnabled : !isEnabled;
}

function useFilteredMenuItems<T extends ConnectMenuItem>({
  menuItems,
  userPermissions,
  isBetaEnabled = false,
  isSpiriiAdminUser = false,
  flagValues = NO_FLAG_VALUES,
}: UseFilteredMenuItemsParams<T>): T[] {
  return useMemo(
    () =>
      menuItems.filter((item) => {
        if (
          item.featureFlag &&
          !isVisibleForFlag(item.featureFlag, flagValues)
        ) {
          return false;
        }

        if (
          !hasAccess({
            anyPermissions: item.permissions,
            anyRoles: item.roles,
            allPermissions: item.allPermissions,
            userPermissions,
          })
        ) {
          return false;
        }

        if (item.spiriiAdminOnly && !isSpiriiAdminUser) {
          return false;
        }

        return !(item.beta && !isBetaEnabled);
      }),
    [menuItems, userPermissions, isBetaEnabled, isSpiriiAdminUser, flagValues]
  );
}

export { useFilteredMenuItems };
export type { MenuFlagValues };
